import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Info } from "@phosphor-icons/react";

const TITLES = { impressum: "Impressum", datenschutz: "Datenschutzerklärung", agb: "AGB" };

export default function LegalModal({ slug, page, primary = "#0E7C66", onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    setData(null);
    setErr(null);
    api
      .get(`/portal/${slug}/legal/${page}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setErr(e.response?.data?.detail || "Inhalt konnte nicht geladen werden."); });
    return () => { active = false; };
  }, [slug, page]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const content = data?.content;
  const tenant = data?.tenant;
  const complete = tenant?.master_data_complete;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50 backdrop-blur-sm animate-[fadeIn_.15s_ease-out]"
      onClick={onClose}
      data-testid="legal-modal-overlay"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid={`legal-modal-${page}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-black/5 flex-shrink-0" style={{ background: `${primary}0D` }}>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight" style={{ color: primary }} data-testid="legal-modal-title">
            {content?.title || TITLES[page] || "Rechtliches"}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors text-zinc-500"
            aria-label="Schließen"
            data-testid="legal-modal-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 sm:px-7 py-6 flex-1" data-testid="legal-modal-body">
          {err && (
            <div className="text-sm text-rose-600" data-testid="legal-modal-error">{err}</div>
          )}
          {!data && !err && (
            <div className="text-sm text-zinc-400 py-8 text-center">Lade…</div>
          )}
          {content && (
            <>
              {!complete && page === "impressum" && (
                <div className="mb-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="legal-modal-incomplete">
                  <Info size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div>Die Firmen-Stammdaten sind noch nicht vollständig hinterlegt. Bitte den Betreiber kontaktieren, falls Angaben fehlen.</div>
                </div>
              )}
              <div className="space-y-7">
                {content.sections.map((s, i) => (
                  <section key={s.heading || i} data-testid={`legal-modal-section-${i}`}>
                    {s.heading && <h3 className="font-heading text-base font-bold tracking-tight mb-2 text-zinc-900">{s.heading}</h3>}
                    {s.lines && (
                      <dl className="space-y-1 text-sm">
                        {s.lines.map((l, j) => (
                          <div key={`${l.label || "x"}:${l.value}`} className="flex gap-3">
                            {l.label && <dt className="text-zinc-500 min-w-[130px]">{l.label}</dt>}
                            <dd className={l.label ? "text-zinc-800" : "text-zinc-800 leading-relaxed"}>{l.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {s.paragraphs && s.paragraphs.map((p) => (
                      <p key={p.slice(0, 40)} className="text-sm text-zinc-700 leading-relaxed mb-2 whitespace-pre-line">{p}</p>
                    ))}
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
